import type { SnackbarTypesType } from 'components-stateless/DesignSystem/Atoms/Snackbar';
import type { ImmerStateCreator } from 'hooks/useStore';

export interface NotificationSlice {
  showNotification: boolean;
  hideNotification: () => void;
  message: string;
  type: SnackbarTypesType;
  actionButtonText: string;
  isCloseable: boolean;
  onClickActionButton: () => void;
  addNotificationError: (param: AddNotificationParams) => void;
  addNotificationInfo: (param: AddNotificationParams) => void;
  addNotificationSuccess: (param: AddNotificationParams) => void;
  addNotificationWarning: (param: AddNotificationParams) => void;
  addNotification: (
    type: SnackbarTypesType,
    param: AddNotificationParams
  ) => void;
  resetNotification: () => void;
}

export interface AddNotificationParams {
  message: string;
  actionButtonText?: string;
  onClickActionButton?: () => void;
}

const useNotificationSlice: ImmerStateCreator<NotificationSlice> = (
  set,
  get
) => ({
  showNotification: false,
  hideNotification: () => {
    get().notificationSlice.resetNotification();
  },
  message: '',
  type: 'Success',
  isCloseable: true,
  actionButtonText: '',
  onClickActionButton: () => {},
  addNotificationError: ({
    message,
    actionButtonText,
    onClickActionButton,
  }: AddNotificationParams) => {
    get().notificationSlice.addNotification('Error', {
      message,
      actionButtonText,
      onClickActionButton,
    });
  },
  addNotificationInfo: ({ message }: AddNotificationParams) => {
    get().notificationSlice.addNotification('Info', {
      message,
    });
  },
  addNotificationSuccess: ({
    message,
    actionButtonText,
    onClickActionButton,
  }: AddNotificationParams) => {
    get().notificationSlice.addNotification('Success', {
      message,
      actionButtonText,
      onClickActionButton,
    });
  },
  addNotificationWarning: ({
    message,
    actionButtonText,
    onClickActionButton,
  }: AddNotificationParams) => {
    get().notificationSlice.addNotification('Warning', {
      message,
      actionButtonText,
      onClickActionButton,
    });
  },
  addNotification: (
    type: SnackbarTypesType,
    { message, actionButtonText, onClickActionButton }: AddNotificationParams
  ) => {
    get().notificationSlice.resetNotification();

    set((state) => {
      state.notificationSlice.message = message;
      state.notificationSlice.type = type;
      state.notificationSlice.showNotification = true;
      if (actionButtonText) {
        state.notificationSlice.actionButtonText = actionButtonText;
      }
      if (onClickActionButton) {
        state.notificationSlice.onClickActionButton = onClickActionButton;
      }
      if (type === 'Info') {
        state.notificationSlice.isCloseable = false;
      }
    });
  },
  resetNotification: () => {
    set((state) => {
      state.notificationSlice.showNotification = false;
      state.notificationSlice.message = '';
      state.notificationSlice.type = 'Success';
      state.notificationSlice.actionButtonText = '';
      state.notificationSlice.isCloseable = true;
      state.notificationSlice.onClickActionButton = () => {};
    });
  },
});

export default useNotificationSlice;
